// ChewHubStatus — tenant-facing view for a submitted maintenance request.
// Lands here from either the confirmation "Check status →" link, or the
// /student page's ref-code shortcut. Reads ticket_id from the route and
// ref code from the query string. Fixes the "loops back to intake" bug
// where the intake component was mounted at /status/:ticketId but ignored
// the URL — so tenants saw a fresh blank form.
import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import {
  CheckCircle, Clock, ChatCircleText, PaperPlaneTilt, Warning,
  Sparkle, House, ArrowLeft, ImageSquare, Camera,
} from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;

const STATUS_STEPS = [
  { key: "submitted",  label: "Received"  },
  { key: "scheduled",  label: "Scheduled" },
  { key: "in_progress",label: "In Progress" },
  { key: "completed",  label: "Completed" },
];

function stepIndex(status) {
  const i = STATUS_STEPS.findIndex(s => s.key === (status || "").toLowerCase());
  return i < 0 ? 0 : i;
}

function niceTime(iso) {
  try { const d = new Date(iso); return d.toLocaleString(); } catch { return "—"; }
}

export default function ChewHubStatus() {
  const { ticketId } = useParams();
  const [sp] = useSearchParams();
  const ref = (sp.get("ref") || "").toUpperCase();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState("");
  const [msg, setMsg]       = useState("");
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef   = useRef(null);
  const cameraRef = useRef(null);

  const uploadPhoto = async (file) => {
    if (!file || uploading) return;
    if (file.size > 15 * 1024 * 1024) { alert("Photo too large (max 15 MB)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("ref", ref);
      fd.append("file", file, file.name || "photo.jpg");
      await axios.post(`${BASE}/api/public/chew/hub/report/${ticketId}/photo-followup`, fd);
      await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      alert(typeof d === "string" ? d : "Photo upload failed — try again.");
    } finally {
      setUploading(false);
      if (fileRef.current)   fileRef.current.value   = "";
      if (cameraRef.current) cameraRef.current.value = "";
    }
  };

  const load = async () => {
    if (!ticketId || !ref) { setError("Missing reference code — try again with your code."); setLoading(false); return; }
    setLoading(true); setError("");
    try {
      const r = await axios.get(`${BASE}/api/public/chew/hub/report/${ticketId}`, { params: { ref } });
      setTicket(r.data);
    } catch (e) {
      // Coerce whatever the backend sent — string, dict, or array — to a
      // safe string so React never blows up rendering the error card.
      const d = e.response?.data?.detail;
      const msg = typeof d === "string" ? d
                : Array.isArray(d) ? (d[0]?.msg || "Not found")
                : d && typeof d === "object" ? (d.msg || JSON.stringify(d))
                : "We couldn't load that ticket.";
      setError(msg);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [ticketId, ref]);   // eslint-disable-line react-hooks/exhaustive-deps

  const sendMessage = async (e) => {
    e?.preventDefault();
    const text = msg.trim();
    if (text.length < 1) return;
    setSending(true);
    try {
      await axios.post(`${BASE}/api/public/chew/hub/report/${ticketId}/message`, { ref, text });
      setMsg(""); await load();
    } catch (e) {
      // Inline error only — don't blow away the whole status page just because
      // one message send failed (they can retry).
      const d = e.response?.data?.detail;
      const m = typeof d === "string" ? d : "Message failed — try again in a sec.";
      alert(m);
    } finally { setSending(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-500 text-sm"
         data-testid="chew-status-loading">Loading your ticket…</div>
  );
  if (error) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white rounded-2xl p-6 text-center space-y-3" data-testid="chew-status-error">
        <Warning weight="fill" size={40} className="text-red-500 mx-auto"/>
        <div className="font-black text-lg">{error}</div>
        <Link to="/student" className="inline-block text-sm font-bold text-cyan-700 hover:underline" data-testid="chew-status-error-back">
          <ArrowLeft size={12} className="inline mr-1"/> Back to sign-in
        </Link>
      </div>
    </div>
  );

  const currentStep = stepIndex(ticket.status);
  // YIQ contrast so black-on-yellow / white-on-navy both stay readable.
  const brand = ticket.brand_color || "#10ADAD";
  const _h = brand.replace("#", "");
  const _r = parseInt(_h.slice(0, 2), 16) || 0;
  const _g = parseInt(_h.slice(2, 4), 16) || 0;
  const _b = parseInt(_h.slice(4, 6), 16) || 0;
  const brandText = (_r * 299 + _g * 587 + _b * 114) / 1000 < 165 ? "#fff" : "#0f172a";

  return (
    <div className="min-h-screen bg-slate-50 pb-10" data-testid="chew-status-page">
      {/* Header */}
      <div className="p-5 sm:p-6" style={{ background: brand, color: brandText }} data-testid="chew-status-brand-header">
        <Link to="/student" className="text-xs opacity-80 hover:opacity-100 inline-flex items-center gap-1 mb-2" data-testid="chew-status-back">
          <ArrowLeft size={12}/> All my requests
        </Link>
        <div className="flex items-start gap-3">
          {ticket.brand_logo_url && (
            <div className="bg-white rounded-lg p-1 shadow-md flex-shrink-0" data-testid="chew-status-brand-logo-wrap">
              <img
                src={ticket.brand_logo_url.startsWith("http") ? ticket.brand_logo_url : `${BASE}${ticket.brand_logo_url}`}
                alt="Landlord logo"
                data-testid="chew-status-brand-logo"
                className="w-12 h-12 rounded object-contain block"
                onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}/>
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-black uppercase tracking-widest opacity-80">
              Chew Flats · Unit {ticket.unit}
            </div>
            <div className="font-display font-black text-2xl sm:text-3xl leading-tight" data-testid="chew-status-subject">
              {ticket.subject || "Maintenance request"}
            </div>
            <div className="text-xs opacity-90 mt-1 flex items-center gap-2 flex-wrap">
              <span className="font-mono bg-white/20 px-2 py-0.5 rounded">{ticket.ref_code}</span>
              <span>· filed {niceTime(ticket.created_at)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-lg mx-auto p-4 space-y-4 -mt-4">
        {/* Progress rail */}
        <div className="bg-white rounded-2xl p-4 shadow-sm" data-testid="chew-status-progress">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-3">Status</div>
          <div className="flex items-center justify-between">
            {STATUS_STEPS.map((s, i) => (
              <div key={s.key} className="flex-1 flex items-center">
                <div className={`h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-black shrink-0 ${
                  i <= currentStep ? "" : "bg-slate-200 text-slate-400"}`}
                  style={i <= currentStep ? { background: brand } : {}}>
                  {i < currentStep ? <CheckCircle weight="fill" size={16}/> :
                   i === currentStep ? <Clock weight="fill" size={16}/> :
                   i + 1}
                </div>
                {i < STATUS_STEPS.length - 1 && (
                  <div className={`flex-1 h-1 mx-1 ${i < currentStep ? "" : "bg-slate-200"}`}
                       style={i < currentStep ? { background: brand } : {}}/>
                )}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-4 mt-2 text-[9px] font-black uppercase tracking-widest text-slate-500 text-center">
            {STATUS_STEPS.map((s, i) => (
              <div key={s.key} className={i <= currentStep ? "text-slate-900" : ""}>{s.label}</div>
            ))}
          </div>
          {ticket.accepted_window && (
            <div className="mt-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs p-2">
              Tech scheduled: <b>{ticket.accepted_window}</b>
            </div>
          )}
        </div>

        {/* Description + priority */}
        <div className="bg-white rounded-2xl p-4 shadow-sm" data-testid="chew-status-detail">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Your request</div>
          <div className="text-sm text-slate-800 whitespace-pre-line">{ticket.description || "—"}</div>
          {ticket.priority && (
            <div className="mt-3 inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded bg-slate-100 text-slate-700">
              <Sparkle weight="fill" size={11}/> Priority · {ticket.priority}
            </div>
          )}
        </div>

        {/* Photos */}
        {ticket.photos?.length > 0 && (
          <div className="bg-white rounded-2xl p-4 shadow-sm" data-testid="chew-status-photos">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2 inline-flex items-center gap-1">
              <ImageSquare size={11}/> Photos · {ticket.photos.length}
            </div>
            <div className="grid grid-cols-3 gap-1.5">
              {ticket.photos.map((p, i) => (
                <div key={i} className="aspect-square bg-slate-100 rounded overflow-hidden">
                  <img src={`${BASE}/api/public/chew/hub/report/${ticketId}/photo/${i}?ref=${ref}`} alt="" className="w-full h-full object-cover"
                       onError={(e) => { e.target.style.display = "none"; }}/>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Conversation */}
        <div className="bg-white rounded-2xl p-4 shadow-sm space-y-3" data-testid="chew-status-thread">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 inline-flex items-center gap-1">
            <ChatCircleText size={11}/> Conversation with the office
          </div>
          {(ticket.messages || []).length === 0 ? (
            <div className="text-xs text-slate-500">No messages yet.</div>
          ) : (
            <div className="space-y-2">
              {ticket.messages.map((m, i) => (
                <div key={i} className={`text-xs rounded-lg p-2 ${
                  m.from === "tenant" ? "bg-slate-100 ml-6" : "bg-cyan-50 mr-6"}`}
                  data-testid={`chew-status-msg-${i}`}>
                  <div className="text-[9px] font-black uppercase tracking-widest opacity-60 mb-0.5">
                    {m.from === "tenant" ? "You" : "Office"} · {niceTime(m.at)}
                  </div>
                  <div className="text-slate-800">{m.text}</div>
                </div>
              ))}
            </div>
          )}
          <form onSubmit={sendMessage} className="flex gap-1.5 items-center">
            <button type="button" onClick={() => cameraRef.current?.click()} disabled={uploading}
              data-testid="chew-status-photo-take"
              title="Take a photo"
              className="h-10 w-10 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 disabled:opacity-50 flex items-center justify-center shrink-0">
              <Camera weight="fill" size={16}/>
            </button>
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
              data-testid="chew-status-photo-pick"
              title="Add from library"
              className="h-10 w-10 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 disabled:opacity-50 flex items-center justify-center shrink-0">
              <ImageSquare weight="fill" size={16}/>
            </button>
            <input value={msg} onChange={e => setMsg(e.target.value)}
              placeholder={uploading ? "Uploading photo…" : "Send a note or add a photo…"}
              maxLength={2000}
              disabled={uploading}
              data-testid="chew-status-msg-input"
              className="flex-1 h-10 border border-slate-300 rounded-lg px-3 text-sm disabled:bg-slate-50"/>
            <button type="submit" disabled={sending || uploading || msg.trim().length < 1}
              data-testid="chew-status-msg-send"
              className="h-10 px-3 rounded-lg text-white text-[11px] font-black uppercase tracking-widest disabled:opacity-50 shrink-0"
              style={{ background: brand }}>
              <PaperPlaneTilt weight="fill" size={13}/>
            </button>
            <input ref={fileRef}   type="file" accept="image/*" className="hidden"
                   data-testid="chew-status-photo-file-input"
                   onChange={e => uploadPhoto(e.target.files?.[0])}/>
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
                   data-testid="chew-status-photo-camera-input"
                   onChange={e => uploadPhoto(e.target.files?.[0])}/>
          </form>
        </div>

        <Link to="/report/chew-flats" data-testid="chew-status-new-request"
          className="block bg-white rounded-2xl p-4 shadow-sm text-center text-sm font-bold text-slate-700 hover:text-slate-900">
          + File another maintenance request
        </Link>
      </div>
    </div>
  );
}
